import React, { useState, useEffect } from 'react';
import api from '../services/api';

const EmailMarketing = () => {
  const [emailLists, setEmailLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'email') {
        const [listsResponse, campaignsResponse] = await Promise.all([
          api.get('/marketing/email/lists'),
          api.get('/marketing/email/campaigns')
        ]);
        setEmailLists(listsResponse.data.lists);
        setCampaigns(campaignsResponse.data.campaigns);
      } else {
        const [accountsResponse, postsResponse] = await Promise.all([
          api.get('/marketing/social/accounts'),
          api.get('/marketing/social/posts')
        ]);
        setSocialAccounts(accountsResponse.data.accounts);
        setSocialPosts(postsResponse.data.posts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      twitter: '🐦',
      instagram: '📷',
      linkedin: '💼',
      tiktok: '🎵',
      youtube: '📺'
    };
    return icons[platform] || '📱';
  };

  const renderEmailMarketing = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Email Lists */}
      <div className="card">
        <h2 className="card-title">Email Lists</h2>
        <div className="grid-1-col gap-3">
          {emailLists.map(list => (
            <div key={list.id} className={`card-sm ${!list.is_active ? 'disabled' : ''}`}>
              <div className="flex justify-between align-start">
                <div className="flex-1">
                  <h3 className="text-base mb-1">{list.name}</h3>
                  <p className="text-sm text-secondary mb-2">
                    {list.description}
                  </p>
                  
                  <div className="text-xs text-secondary">
                    Created: {new Date(list.created_at).toLocaleDateString()}
                    {list.double_opt_in && <span> | Double Opt-in</span>}
                  </div>
                </div>
                
                <div className={`badge ${list.is_active ? 'success' : 'danger'}`}>
                  {list.is_active ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>

              <div className="stats-grid-3">
                <div className="stat-item">
                  <div className="stat-value primary">{list.subscriber_count.toLocaleString()}</div>
                  <div className="stat-label">Total</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value success">{list.active_subscribers.toLocaleString()}</div>
                  <div className="stat-label">Active</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value danger">{Math.round(list.unsubscribe_rate * 100)}%</div>
                  <div className="stat-label">Unsubscribe</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="card">
        <h2 className="card-title">Recent Email Campaigns</h2>
        <div className="grid-1-col gap-3">
          {campaigns.slice(0, 5).map(campaign => (
            <div key={campaign.id} className="card-sm">
              <div className="flex justify-between align-start mb-2">
                <div>
                  <h4 className="text-sm mb-1">{campaign.name}</h4>
                  <div className="text-xs text-secondary">
                    {campaign.subject_line}
                  </div>
                </div>
                <div className={`badge ${campaign.status}`}>
                  {campaign.status.toUpperCase()}
                </div>
              </div>

              {campaign.status === 'sent' && (
                <div className="stats-grid-2 mb-2">
                  <div>
                    <strong>Delivered:</strong> {campaign.delivered_count?.toLocaleString()}
                  </div>
                  <div>
                    <strong>Opened:</strong> {Math.round(campaign.open_rate)}%
                  </div>
                  <div>
                    <strong>Clicked:</strong> {Math.round(campaign.click_rate)}%
                  </div>
                  <div>
                    <strong>Unsubscribed:</strong> {campaign.unsubscribed_count}
                  </div>
                </div>
              )}

              <div className="text-xs text-secondary">
                {campaign.scheduled_at 
                  ? `Scheduled: ${new Date(campaign.scheduled_at).toLocaleString()}`
                  : `Created: ${new Date(campaign.created_at).toLocaleDateString()}`
                }
              </div>
            </div>
          ))}
        </div>
      </div>

    <div className="card">
      <h2 className="card-title">Campaign Performance Overview</h2>
        <div className="grid-1-col gap-4">
          {campaigns.map(campaign => (
            <div key={campaign.id} className="card-sm">
              <div className="flex justify-between align-start mb-3">
                <div className="flex-1">
                  <h3 className="text-lg mb-1">{campaign.name}</h3>
                  <div className="text-sm text-secondary">
                    {campaign.subject_line}
                  </div>
                </div>
                <div className={`badge ${campaign.status}`}>
                  {campaign.status.toUpperCase()}
                </div>
              </div>

              {campaign.status === 'sent' && (
                <div className="stats-grid-5 mb-3">
                  <div className="stat-item">
                    <div className="stat-value primary">{campaign.recipients_count.toLocaleString()}</div>
                    <div className="stat-label">Recipients</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value success">{campaign.delivered_count.toLocaleString()}</div>
                    <div className="stat-label">Delivered</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value warning">{Math.round(campaign.open_rate)}%</div>
                    <div className="stat-label">Open Rate</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value purple">{Math.round(campaign.click_rate)}%</div>
                    <div className="stat-label">Click Rate</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value danger">{campaign.unsubscribed_count}</div>
                    <div className="stat-label">Unsubscribed</div>
                  </div>
                </div>
              )}

              <div className="text-xs text-secondary">
                Created: {new Date(campaign.created_at).toLocaleString()}
                {campaign.scheduled_at && (
                  <span> | Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSocialMedia = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      
        {/* Social Accounts */}
        <div className="card">
          <h2 className="card-title">Social Media Accounts</h2>
          <div className="grid-1-col gap-3">
          {socialAccounts.map(account => (
            <div key={account.id} className={`card-sm ${!account.is_active ? 'disabled' : ''}`}>
              <div className="flex justify-between align-start">
                <div className="flex-1">
                  <div className="flex align-center gap-2 mb-2">
                    <span className="text-xl">
                      {getPlatformIcon(account.platform)}
                    </span>
                    <div>
                      <h3 className="text-base mb-0">{account.account_name}</h3>
                      <div className="text-xs text-secondary">
                        {account.platform.toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className={`badge ${account.is_active ? 'success' : 'danger'}`}>
                  {account.is_active ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>

              <div className="stats-grid-3">
                <div className="stat-item">
                  <div className="stat-value primary">{account.follower_count.toLocaleString()}</div>
                  <div className="stat-label">Followers</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value success">{account.following_count.toLocaleString()}</div>
                  <div className="stat-label">Following</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value warning">{account.post_count}</div>
                  <div className="stat-label">Posts</div>
                </div>
              </div>

              {account.auto_post && (
                <div className="badge purple full-width text-center">
                  Auto-posting enabled
                </div>
              )}
            </div>
          ))}
        </div>
        </div>

        {/* Recent Posts */}
        <div className="card">
          <h2 className="card-title">Recent Social Posts</h2>
        <div className="grid-1-col gap-3">
          {socialPosts.slice(0, 5).map(post => (
            <div key={post.id} className="card-sm">
              <div className="flex justify-between align-start mb-2">
                <div className="flex align-center gap-2">
                  <span className="text-base">
                    {getPlatformIcon(post.platform)}
                  </span>
                  <div className="text-xs text-secondary">
                    {post.platform.toUpperCase()}
                  </div>
                </div>
                <div className={`badge ${post.status}`}>
                  {post.status.toUpperCase()}
                </div>
              </div>

              <div className="content-preview mb-2">
                {post.content.substring(0, 120)}
                {post.content.length > 120 && '...'}
              </div>

              {post.status === 'published' && (
                <div className="stats-grid-3 mb-2">
                  <div>
                    <strong>Likes:</strong> {post.likes?.toLocaleString()}
                  </div>
                  <div>
                    <strong>Comments:</strong> {post.comments?.toLocaleString()}
                  </div>
                  <div>
                    <strong>Shares:</strong> {post.shares?.toLocaleString()}
                  </div>
                </div>
              )}

              <div className="text-xs text-secondary">
                {post.published_at 
                  ? `Published: ${new Date(post.published_at).toLocaleDateString()}`
                  : post.scheduled_for
                    ? `Scheduled: ${new Date(post.scheduled_for).toLocaleDateString()}`
                    : `Created: ${new Date(post.created_at).toLocaleDateString()}`
                }
              </div>
            </div>
          ))}
        </div>
        </div>
      
      <div className="card">
        <h2 className="card-title">Social Media Performance</h2>
      <div className="grid-1-col gap-4">{socialPosts.filter(post => post.status === 'published').map(post => (
          <div key={post.id} className="card-sm">
            <div className="flex justify-between align-start mb-3">
              <div className="flex-1">
                <div className="flex align-center gap-2 mb-2">
                  <span className="text-xl">
                    {getPlatformIcon(post.platform)}
                  </span>
                  <h3 className="text-base mb-0">{post.platform.toUpperCase()}</h3>
                </div>
                
                <div className="content-preview">
                  {post.content}
                </div>
              </div>
            </div>

            <div className="stats-grid-5">
              <div className="stat-item">
                <div className="stat-value primary">{post.reaches?.toLocaleString() || '0'}</div>
                <div className="stat-label">Reach</div>
              </div>
              <div className="stat-item">
                <div className="stat-value success">{post.likes?.toLocaleString() || '0'}</div>
                <div className="stat-label">Likes</div>
              </div>
              <div className="stat-item">
                <div className="stat-value warning">{post.comments?.toLocaleString() || '0'}</div>
                <div className="stat-label">Comments</div>
              </div>
              <div className="stat-item">
                <div className="stat-value purple">{post.shares?.toLocaleString() || '0'}</div>
                <div className="stat-label">Shares</div>
              </div>
              <div className="stat-item">
                <div className="stat-value danger">{Math.round(post.engagement_rate)}%</div>
                <div className="stat-label">Engagement</div>
              </div>
            </div>

            <div className="text-xs text-secondary mt-3">
              Published: {new Date(post.published_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading marketing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Email & Social Media
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Email & Social Media Marketing
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage email campaigns and social media marketing
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)'
          }}>
            <button
            onClick={() => setActiveTab('email')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${activeTab === 'email' ? 'var(--primary-600)' : 'transparent'}`,
              cursor: 'pointer',
              fontWeight: '600',
              color: activeTab === 'email' ? 'var(--primary-600)' : 'var(--gray-500)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            📧 Email Marketing ({emailLists.length} lists, {campaigns.length} campaigns)
          </button>
          <button
            onClick={() => setActiveTab('social')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${activeTab === 'social' ? 'var(--primary-600)' : 'transparent'}`,
              cursor: 'pointer',
              fontWeight: '600',
              color: activeTab === 'social' ? 'var(--primary-600)' : 'var(--gray-500)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            📱 Social Media ({socialAccounts.length} accounts, {socialPosts.length} posts)
          </button>
          </div>
        </div>

        {activeTab === 'email' ? renderEmailMarketing() : renderSocialMedia()}
      </div>
    </div>
  );
};

export default EmailMarketing;